import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * User preferences — theme + UI language. Persisted to localStorage so the
 * choice survives logout/login on the same device. Backend persistence
 * could be layered on later by syncing this store with `PUT /profile`.
 */

export type AppLanguage = 'en' | 'es';

export type ThemeId = 'xotski' | 'volt' | 'nebula' | 'orchid' | 'mist';

export interface ThemeMeta {
  id: ThemeId;
  label: string;
  /** Grouping in the picker. 'brand' = dark frame around a light canvas. */
  mode: 'brand' | 'dark' | 'light';
  /** Preview swatches shown in the menu (bg, surface, accent). */
  preview: { bg: string; surface: string; accent: string };
}

/**
 * `xotski` is the canonical brand theme: near-black chrome, off-white canvas,
 * lime CTA, no purple anywhere.
 *
 * The four themes below it derive from the Xamplin brand palette (5 colors):
 *   #1B0F2B dark · #6C1CD3 deep purple · #B262F5 violet ·
 *   #E2DFF2 light lavender · #C8E530 Xotski lime.
 * They are kept as opt-in alternatives; each combo maps to one theme
 * (2 dark + 2 light).
 */
export const THEMES: ThemeMeta[] = [
  { id: 'xotski', label: 'Xotski', mode: 'brand', preview: { bg: '#0e0e0e', surface: '#ffffff', accent: '#c8e530' } },
  { id: 'volt', label: 'Volt', mode: 'dark', preview: { bg: '#1b0f2b', surface: '#251640', accent: '#c8e530' } },
  { id: 'nebula', label: 'Nebula', mode: 'dark', preview: { bg: '#1b0f2b', surface: '#241638', accent: '#6c1cd3' } },
  { id: 'orchid', label: 'Orchid', mode: 'light', preview: { bg: '#e2dff2', surface: '#ffffff', accent: '#b262f5' } },
  { id: 'mist', label: 'Mist', mode: 'light', preview: { bg: '#f3f1fa', surface: '#ffffff', accent: '#6c1cd3' } },
];

const THEME_IDS: ThemeId[] = ['xotski', 'volt', 'nebula', 'orchid', 'mist'];

/** Default theme — the Xotski brand palette. */
const DEFAULT_THEME: ThemeId = 'xotski';

export const LANGUAGES: { id: AppLanguage; label: string; flag: string }[] = [
  { id: 'en', label: 'English', flag: 'EN' },
  { id: 'es', label: 'Español', flag: 'ES' },
];

/**
 * The language to start in on a device that has never chosen one.
 *
 * It used to be hardcoded to English, so a browser set to Spanish met an
 * English admin — and a Spanish-speaking manager had to find the language
 * picker in Settings before they could read the app that was supposed to help
 * them. The browser already states this preference; asking it is free.
 *
 * Only the FIRST run is affected: `persist` rehydrates over this initialiser,
 * so an explicit choice always wins and is never re-guessed.
 *
 * `navigator.languages` is ordered by preference and holds tags like `es-ES`,
 * so we match on the primary subtag. Guarded for the non-browser callers
 * (tests, Storybook's node build) where `navigator` is absent.
 */
export function detectLanguage(): AppLanguage {
  const supported = new Set<AppLanguage>(['en', 'es']);
  const candidates =
    typeof navigator === 'undefined'
      ? []
      : [...(navigator.languages ?? []), navigator.language].filter(Boolean);

  for (const tag of candidates) {
    const primary = String(tag).toLowerCase().split('-')[0] as AppLanguage;
    if (supported.has(primary)) return primary;
  }
  return 'en';
}

interface PrefsState {
  theme: ThemeId;
  language: AppLanguage;
  setTheme: (id: ThemeId) => void;
  setLanguage: (id: AppLanguage) => void;
}

export const usePrefsStore = create<PrefsState>()(
  persist(
    (set) => ({
      theme: DEFAULT_THEME,
      language: detectLanguage(),
      setTheme: (theme) => set({ theme }),
      setLanguage: (language) => set({ language }),
    }),
    {
      name: 'xotski.prefs',
      version: 2,
      // v1 → v2 rolls every device onto the Xotski brand theme. The bump is
      // deliberate: the four legacy palettes are purple-based, and devices
      // that had already persisted one would otherwise never see the
      // rebrand. Users can re-pick a legacy theme afterwards and that choice
      // sticks (it is only reset again by another version bump).
      //
      // The unknown-id guard also stays useful on its own: older devices
      // persisted theme ids that no longer exist (ink/cobalt/…), and
      // <html data-theme> must never point at a missing theme block.
      migrate: (state, fromVersion) => {
        const s = state as Partial<PrefsState> | undefined;
        if (fromVersion < 2) return { ...s, theme: DEFAULT_THEME } as PrefsState;
        const theme = s?.theme && THEME_IDS.includes(s.theme) ? s.theme : DEFAULT_THEME;
        return { ...s, theme } as PrefsState;
      },
    },
  ),
);

/**
 * Applies the current theme + language to <html> so CSS variable swaps take
 * effect everywhere. Mounted once at the app shell level.
 */
export function ThemeApplier() {
  const theme = usePrefsStore((s) => s.theme);
  const language = usePrefsStore((s) => s.language);
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    root.lang = language;
  }, [theme, language]);
  return null;
}
